import { PropsWithChildren, ReactNode } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { A11y, Mousewheel } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import {
  Box,
  Card,
  CardProps,
  Flex,
  Heading,
  LinkBox,
  LinkOverlay,
  List,
  ListItem,
  Tag,
  TagLabel,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { SanityImage } from '~/components/sanity-image';
import { trackEvent } from '~/features/analytics/track-event';
import { useI18n } from '~/hooks/use-i18n';

import { BookIcon, BriefCaseIcon, FilmIcon, LightBulbIcon, MicrophoneIcon } from '../icons';

import { SectionHeader } from './section-header';

import 'swiper/css';
import 'swiper/css/mousewheel';

export type UdacityResourcesSectionProps = StaticUdacityResourcesSectionProps;

export function UdacityResourcesSection({ resources, martyCaganResource }: UdacityResourcesSectionProps) {
  const { t } = useTranslation();
  const CAROUSEL_CARD_HEIGHT = '320px';
  const CAROUSEL_CARD_WIDTH = '302px';

  const displayResources = martyCaganResource
    ? resources.map((r) =>
        r.type === 'media' && r.titleKey === 'home.resources.items.vibeEngineeringVideo.title' ? martyCaganResource : r
      )
    : resources;

  const vibeEngineeringResource = resources.find(
    (r) => r.type === 'media' && r.titleKey === 'home.resources.items.vibeEngineeringVideo.title'
  );
  const featuredCarouselCard = martyCaganResource ?? vibeEngineeringResource;
  const carouselResources = featuredCarouselCard
    ? [featuredCarouselCard, ...displayResources.filter((r) => r.titleKey !== featuredCarouselCard.titleKey)]
    : displayResources;

  function handleResourceClick(resourceTitle: string) {
    trackEvent({
      type: 'click',
      name: 'Homepage CTA Clicked',
      label: 'resource_center_clicked',
      value: resourceTitle,
    });
  }

  function renderCard(resource: UdacityResource, isMasonry = false) {
    switch (resource.type) {
      case 'case-study':
        return (
          <UdacityResourceCardLayout
            backgroundImageSlot={
              resource.imagePath && (
                <Image
                  alt=''
                  loading='lazy'
                  src={resource.imagePath}
                  style={{ objectFit: 'cover', pointerEvents: 'none' }}
                  fill
                />
              )
            }
            backgroundOverlaySlot={
              <Box bgGradient='linear(204.55deg, #6100B0 0%, #0A0B0F 58.13%)' inset={0} position='absolute' />
            }
            cardContentSlot={
              <Flex flexDirection='column' gap={4} height='100%' justifyContent='space-between'>
                <Flex gap={4} justifyContent='space-between'>
                  <UdacityResourceCardTag resourceType={resource.type} />
                  {resource.logo ? (
                    <SanityImage alt='' height={56} image={resource.logo} width={72} />
                  ) : (
                    resource.logoPath && (
                      <Image
                        alt='Udacity'
                        height={56}
                        src={resource.logoPath}
                        style={{ objectFit: 'contain', objectPosition: 'right top' }}
                        width={72}
                      />
                    )
                  )}
                </Flex>
                <Flex flexDirection='column' gap={4}>
                  <LinkOverlay
                    color='white'
                    fontFamily='heading'
                    fontWeight='600'
                    href={resource.path}
                    lineHeight='1.2'
                    noOfLines={6}
                    fontSize={{
                      base: '1.25rem',
                      md: '1.5rem',
                    }}
                    onClick={() => handleResourceClick(t(resource.titleKey, { lng: 'en-US' }))}
                  >
                    {t(resource.titleKey)}
                  </LinkOverlay>
                  <Text color='grey.300' fontSize='0.8125rem' lineHeight='1.4' noOfLines={2}>
                    {t(resource.descriptionKey)}
                  </Text>
                </Flex>
              </Flex>
            }
            cardProps={{
              height: isMasonry ? '352px' : CAROUSEL_CARD_HEIGHT,
              width: isMasonry ? undefined : CAROUSEL_CARD_WIDTH,
              ...resource.cardPropsOverride,
            }}
          />
        );
      case 'report':
        return (
          <UdacityResourceCardLayout
            backgroundImageSlot={
              <Image
                alt=''
                loading='lazy'
                src='/images/experiments/ab_plp_redesign_2025/ripple.svg'
                style={{
                  objectFit: 'cover',
                  objectPosition: 'top right',
                  mixBlendMode: 'overlay',
                  pointerEvents: 'none',
                }}
                fill
              />
            }
            cardContentSlot={
              <Flex flexDirection='column' gap={4} height='100%' justifyContent='space-between'>
                <UdacityResourceCardTag resourceType={resource.type} />
                <Flex flexDirection='column' gap={4}>
                  <LinkOverlay
                    color='white'
                    fontFamily='heading'
                    fontSize='1.25rem'
                    fontWeight='600'
                    href={resource.path}
                    lineHeight='1.2'
                    noOfLines={4}
                    onClick={() => handleResourceClick(t(resource.titleKey))}
                  >
                    {t(resource.titleKey)}
                  </LinkOverlay>
                  <Text color='grey.300' fontSize='0.8125rem' lineHeight='1.4' noOfLines={2}>
                    {t(resource.descriptionKey)}
                  </Text>
                </Flex>
              </Flex>
            }
            cardProps={{
              height: isMasonry ? '264px' : CAROUSEL_CARD_HEIGHT,
              width: isMasonry ? undefined : CAROUSEL_CARD_WIDTH,
              bgColor: 'grey.900',
              bgGradient: 'radial(100% 114.86% at 0% 0%, rgba(238, 118, 34, 0.264) 0%, rgba(238, 118, 34, 0) 100%)',
              ...resource.cardPropsOverride,
            }}
          />
        );
      case 'webinar':
        return (
          <UdacityResourceCardLayout
            cardContentSlot={
              <Flex flexDirection='column' gap={4} height='100%' justifyContent='space-between'>
                <UdacityResourceCardTag resourceType={resource.type} />
                <Flex flexDirection='column' gap={4}>
                  <Image
                    alt=''
                    height={56}
                    loading='lazy'
                    src={`/images/experiments/ab_plp_redesign_2025/audio-wave-${resource.theme}.png`}
                    width={246}
                    style={{
                      objectFit: 'contain',
                      objectPosition: 'center',
                      pointerEvents: 'none',
                      margin: '0 auto',
                      display: 'block',
                    }}
                  />
                </Flex>
                <Flex flexDirection='column' gap={4}>
                  <LinkOverlay
                    color='white'
                    fontFamily='heading'
                    fontSize='1.25rem'
                    fontWeight='600'
                    href={resource.path}
                    lineHeight='1.2'
                    noOfLines={3}
                    onClick={() => handleResourceClick(t(resource.titleKey))}
                  >
                    {t(resource.titleKey)}
                  </LinkOverlay>
                  <Text color='grey.300' fontSize='0.8125rem' lineHeight='1.4' noOfLines={2}>
                    {t(resource.descriptionKey)}
                  </Text>
                </Flex>
              </Flex>
            }
            cardProps={{
              height: isMasonry ? '288px' : CAROUSEL_CARD_HEIGHT,
              width: isMasonry ? undefined : CAROUSEL_CARD_WIDTH,
              bgGradient: `linear(135deg, ${resource.theme === 'blue' ? 'rgba(14, 32, 79, 1)' : 'rgba(54, 17, 89, 1)'} 0%, rgba(10, 11, 15, 1) 100%)`,
              ...resource.cardPropsOverride,
            }}
          />
        );
      case 'media':
        return (
          <UdacityResourceCardLayout
            backgroundImageSlot={
              <Image
                alt=''
                loading='lazy'
                src='/images/experiments/ab_plp_redesign_2025/diagonal-lines.svg'
                style={{
                  objectPosition: 'top right',
                  pointerEvents: 'none',
                  objectFit: 'cover',
                  mixBlendMode: 'overlay',
                }}
                fill
              />
            }
            backgroundOverlaySlot={
              <Box
                background='linear-gradient(180deg, rgba(10, 11, 15, 0) 0%, #0A0B0F 100%)'
                inset={0}
                pointerEvents='none'
                position='absolute'
              />
            }
            cardContentSlot={
              <Flex flexDirection='column' gap={4} height='100%' justifyContent='space-between'>
                <UdacityResourceCardTag resourceType={resource.type} />
                <Flex
                  backgroundColor='hsla(0, 0%, 0%, 0.64)'
                  borderRadius='4px'
                  flexDirection='column'
                  gap={4}
                  padding={3}
                >
                  <LinkOverlay
                    color='white'
                    fontFamily='heading'
                    fontSize='1.25rem'
                    fontWeight='600'
                    href={resource.path}
                    lineHeight='1.2'
                    noOfLines={4}
                    onClick={() => handleResourceClick(t(resource.titleKey))}
                  >
                    {t(resource.titleKey)}
                  </LinkOverlay>
                  <Text color='grey.300' fontSize='0.8125rem' lineHeight='1.4' noOfLines={2}>
                    {t(resource.descriptionKey)}
                  </Text>
                </Flex>
              </Flex>
            }
            cardProps={{
              height: isMasonry ? '376px' : CAROUSEL_CARD_HEIGHT,
              width: isMasonry ? undefined : CAROUSEL_CARD_WIDTH,
              bgGradient: 'linear(0deg, blue.900, blue.500)',
              ...resource.cardPropsOverride,
            }}
            imageSlot={
              <>
                {!!resource.imagePath && (
                  <Image
                    alt=''
                    loading='lazy'
                    quality={80}
                    sizes='100vw'
                    src={resource.imagePath}
                    style={{ objectFit: 'cover', pointerEvents: 'none' }}
                    fill
                  />
                )}
                <Box
                  background='linear-gradient(180deg,rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.8) 100%)'
                  inset={0}
                  pointerEvents='none'
                  position='absolute'
                />
              </>
            }
          />
        );
      default:
        return null;
    }
  }

  return (
    <Box>
      <Box
        bgGradient='linear(180deg, #000B26 0%, #00298C 100%)'
        borderRadius={{
          md: '12px',
        }}
        marginX={{
          base: '-16px',
          md: 'unset',
        }}
        padding={{
          base: '40px 16px 96px',
          md: '40px 56px 208px',
        }}
      >
        <SectionHeader
          color='cyan.300'
          descriptionSlot={<Text color='grey.300'>{t('home.resources.description')}</Text>}
          iconSlot={<LightBulbIcon color='cyan.300' height='15px' width='15px' />}
          subtitle={t('home.resources.subtitle') ?? ''}
          titleSlot={
            <Heading
              as='h2'
              color='white'
              fontWeight='600'
              lineHeight='1.1'
              fontSize={{
                base: '1.75rem',
                md: '3.375rem',
              }}
            >
              {t('home.resources.title')}
            </Heading>
          }
        />
      </Box>
      <Flex
        flexDirection='column'
        gap={{
          base: 6,
          md: 12,
        }}
      >
        {/* Desktop Masonry Layout */}
        <List
          margin='-148px auto 0'
          maxWidth='936px'
          width='100%'
          display={{
            base: 'none',
            md: 'block',
          }}
          sx={{
            columnCount: 3,
            columnGap: '24px',
          }}
        >
          {displayResources.map((resource) => {
            const card = renderCard(resource, true);
            if (!card) return null;
            return (
              <ListItem
                key={resource.titleKey}
                sx={{
                  breakInside: 'avoid',
                }}
              >
                {card}
              </ListItem>
            );
          })}
        </List>
        {/* Mobile Carousel Layout */}
        <UdacityResourceCardCarousel initialSlide={0}>
          {carouselResources.map((resource) => {
            const card = renderCard(resource);
            if (!card) return null;
            return (
              <SwiperSlide key={resource.titleKey} style={{ width: CAROUSEL_CARD_WIDTH, height: CAROUSEL_CARD_HEIGHT }}>
                {card}
              </SwiperSlide>
            );
          })}
        </UdacityResourceCardCarousel>

        <ButtonLink
          buttonProps={{
            variant: 'solid',
            colorScheme: 'blue',
            leftIcon: <LightBulbIcon height='18px' width='18px' />,
            width: {
              base: '100%',
              md: 'fit-content',
            },
            margin: '0 auto',
            onClick: () => handleResourceClick('explore_all_resources'),
          }}
          linkProps={{
            href: '/resource-center',
          }}
        >
          {t('common.exploreAllResources')}
        </ButtonLink>
      </Flex>
    </Box>
  );
}

function UdacityResourceCardCarousel({ children, initialSlide = 1 }: PropsWithChildren<{ initialSlide?: number }>) {
  const { i18n } = useI18n();
  /**
   * Note: `isMobile` is necessary as a workaround to avoid the error when rendering the Swiper carousel on _only_ mobile.
   * The error arises when the parent container is hidden and we are trying to use `centeredSlides` in the Swiper.
   */
  const isMobile = useBreakpointValue(
    {
      base: true,
      md: false,
    },
    { fallback: 'md' }
  );

  return (
    <Box
      marginTop='-55px'
      marginX='-16px'
      display={{
        base: 'block',
        md: 'none',
      }}
      sx={{
        '.swiper .swiper-slide': {
          height: 'auto',
          display: 'flex',
        },
      }}
    >
      <Swiper
        key={isMobile ? 'mobile' : 'desktop'}
        centeredSlides={isMobile}
        direction='horizontal'
        initialSlide={initialSlide}
        modules={[A11y, Mousewheel]}
        slidesPerView='auto'
        spaceBetween={24}
        mousewheel={{
          forceToAxis: true,
          thresholdDelta: 10,
        }}
        loop
        onBeforeInit={(swiper) => {
          swiper.changeLanguageDirection(i18n.dir());
        }}
      >
        {children}
      </Swiper>
    </Box>
  );
}

type UdacityResourceCardLayoutProps = {
  backgroundOverlaySlot?: ReactNode;
  backgroundImageSlot?: ReactNode;
  imageSlot?: ReactNode;
  cardContentSlot?: ReactNode;
  cardProps?: CardProps;
};
function UdacityResourceCardLayout({
  backgroundOverlaySlot = null,
  backgroundImageSlot = null,
  imageSlot = null,
  cardContentSlot = null,
  cardProps,
}: UdacityResourceCardLayoutProps) {
  return (
    <Card
      {...cardProps}
      as={LinkBox}
      border='none'
      boxShadow='none'
      isolation='isolate'
      mb={6}
      overflow='hidden'
      position='relative'
    >
      {backgroundOverlaySlot}
      {backgroundImageSlot && (
        <Box inset={0} position='absolute'>
          {backgroundImageSlot}
        </Box>
      )}
      {imageSlot && (
        <Box inset={0} position='absolute'>
          {imageSlot}
        </Box>
      )}
      <LinkBox inset='0px' padding={4} position='absolute'>
        {cardContentSlot}
      </LinkBox>
    </Card>
  );
}

function UdacityResourceCardTag({ resourceType }: { resourceType: UdacityResource['type'] }) {
  const { t } = useTranslation();
  return (
    <Tag
      alignSelf='flex-start'
      bg='hsla(0, 34.50%, 46.10%, 0.16)'
      border='none'
      borderRadius='4px'
      boxShadow='none'
      color='white'
      fontFamily='condensed'
      fontWeight='400'
      gap={2}
      padding='6px 12px 6px 8px'
      textTransform='uppercase'
    >
      {resourceType === 'media' && <FilmIcon height='14px' width='14px' />}
      {resourceType === 'webinar' && <MicrophoneIcon height='14px' width='14px' />}
      {resourceType === 'report' && <BookIcon height='14px' width='14px' />}
      {resourceType === 'case-study' && <BriefCaseIcon height='14px' width='14px' />}
      <TagLabel fontSize='0.875rem' letterSpacing='1px' lineHeight='1'>
        {resourceType === 'case-study' ? t('common.caseStudy') : resourceType}
      </TagLabel>
    </Tag>
  );
}

type UdacityResource = (
  | StaticUdacityResourcesSectionProps['resources'][number]
  | StaticUdacityResourcesSectionProps['martyCaganResource']
) & { cardPropsOverride?: Partial<CardProps> };

export async function transformStaticUdacityResourcesSectionProps() {
  const { getCompany } = await import('~/features/companies/controllers/get-company');

  return {
    resources: [
      {
        type: 'case-study' as const,
        titleKey: 'home.resources.items.elmGraduateProgram.title',
        descriptionKey: 'home.resources.items.elmGraduateProgram.description',
        path: '/enterprise/case-study/elm',
        imagePath: '/images/experiments/ab_plp_redesign_2025/street-bg.png',
        logo: await getCompany('Elm').then((company) => company?.lofiLogoLight),
      },
      {
        type: 'webinar' as const,
        titleKey: 'home.resources.items.aiAgentsWebinar.title',
        descriptionKey: 'home.resources.items.aiAgentsWebinar.description',
        path: '/video/agentic-ai-myths-replay',
        theme: 'blue',
      },
      {
        type: 'report' as const,
        titleKey: 'home.resources.items.productLeadersReport.title',
        descriptionKey: 'home.resources.items.productLeadersReport.description',
        path: '/report/generative-ai-for-product-leaders',
      },
      {
        type: 'media' as const,
        titleKey: 'home.resources.items.vibeEngineeringVideo.title',
        descriptionKey: 'home.resources.items.vibeEngineeringVideo.description',
        path: '/video/what-is-vibe-engineering',
        imagePath: '/images/experiments/ab_plp_redesign_2025/joe-fontaine.png',
      },
      {
        type: 'case-study' as const,
        titleKey: 'home.resources.items.globalTechCaseStudy.title',
        descriptionKey: 'home.resources.items.globalTechCaseStudy.description',
        path: '/enterprise/case-study/global-tech-and-eng-case-study',
        logoPath: '/images/logos/udacity-icon.svg',
        imagePath: '/images/experiments/ab_plp_redesign_2025/skyscrapers-bg.png',
      },
      {
        type: 'report' as const,
        titleKey: 'home.resources.items.stateOfAiReport2025.title',
        descriptionKey: 'home.resources.items.stateOfAiReport2025.description',
        path: '/report/state-of-ai-at-work-report-2025',
      },
    ],
    martyCaganResource: {
      type: 'media' as const,
      titleKey: 'home.resources.items.martyCaganWebinar.title',
      descriptionKey: 'home.resources.items.martyCaganWebinar.description',
      path: '/video/new-standard-for-product-managers-replay',
      imagePath: '/images/accreditation/marty-cagan.png',
      cardPropsOverride: {
        bgGradient: 'linear(-77.76deg, #540916 0%, #0A0B0F 100%)',
      },
    },
  };
}

export type StaticUdacityResourcesSectionProps = Awaited<
  ReturnType<typeof transformStaticUdacityResourcesSectionProps>
>;
